/* eslint-disable @typescript-eslint/strict-boolean-expressions */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */

import React from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import './LoanDataDisplay.scss';
import fmt from 'indian-number-format';


interface CashFlowItem {
  Title: string;
  Code: string;
  Cash: number | string;
  Bank: number | string;
}

interface CashFlowApiResponse {
  responseData: CashFlowItem[];
  type: string;
  branch: string;
  startDate: string;
  endDate: string;
}

interface CashFlowReportProps {
  data: CashFlowApiResponse | CashFlowItem[];
}


const isSectionHeader = (item: CashFlowItem): boolean =>
  !!item.Code && !item.Code.includes('.') && item.Cash === '' && item.Bank === '';

// Depth from the code's dotted segments: "3" -> 0, "3.1" -> 1, "3.1.1" -> 2
const codeDepth = (code: string): number =>
  code ? code.split('.').length - 1 : 0;

const isSummaryRow = (item: CashFlowItem): boolean =>
  !item.Code &&
  (item.Title === 'Total Outflow' || item.Title === 'Derived Balance' || item.Title === "Difference" || item.Title === 'Total Inflow' || item.Title === 'Closing Balance');

const formatAmount = (val: number | string): string => {
  if (val === '' || val === null || val === undefined) return '—';
  const num = Number(val);
  if (isNaN(num)) return '—';
  return fmt.format(num);
};

const isApiResponse = (
  d: CashFlowApiResponse | CashFlowItem[]
): d is CashFlowApiResponse =>
  !Array.isArray(d) && d != null && 'responseData' in d;

const columns: ColumnsType<CashFlowItem> = [
  {
    title: 'Code',
    dataIndex: 'Code',
    key: 'Code',
    width: 90,
    render: (code: string) => code || '',
  },
  {
    title: 'Particulars',
    dataIndex: 'Title',
    key: 'Title',
    render: (title: string, record: CashFlowItem) => {
      const indent = codeDepth(record.Code) * 20;
      if (isSectionHeader(record) || isSummaryRow(record)) {
        return <strong style={{ paddingLeft: indent }}>{title}</strong>;
      }
      return <span style={{ paddingLeft: indent }}>{title}</span>;
    },
  },
  {
    title: 'Cash (₹)',
    dataIndex: 'Cash',
    key: 'Cash',
    align: 'right',
    width: 140,
    render: (val: number | string, record: CashFlowItem) => {
      if (isSectionHeader(record)) return '';
      const formatted = formatAmount(val);
      if (isSummaryRow(record)) {
        return <strong>{formatted}</strong>;
      }
      return formatted;
    },
  },
  {
    title: 'Bank (₹)',
    dataIndex: 'Bank',
    key: 'Bank',
    align: 'right',
    width: 140,
    render: (val: number | string, record: CashFlowItem) => {
      if (isSectionHeader(record)) return '';
      const formatted = formatAmount(val);
      if (isSummaryRow(record)) {
        return <strong>{formatted}</strong>;
      }
      return formatted;
    },
  },
];


const CashFlowReportDisplay: React.FC<CashFlowReportProps> = ({ data }) => {
  const responseData: CashFlowItem[] = isApiResponse(data)
    ? (data.responseData ?? [])
    : Array.isArray(data)
    ? data
    : [];

  const branch = isApiResponse(data) ? data.branch : '';
  const startDate = isApiResponse(data) ? data.startDate : '';
  const endDate = isApiResponse(data) ? data.endDate : '';

  const tableData = responseData.filter(
    (item) => item.Title !== '' || item.Code !== ''
  );

  const rowClassName = (record: CashFlowItem): string => {
    if (isSectionHeader(record)) return 'cf-section-header';
    if (isSummaryRow(record)) return 'cf-summary-row';
    return 'cf-data-row';
  };

  return (
    <div className="loan-data-container">
      <h2>Cash Flow Report</h2>

      {(branch || startDate || endDate) && (
        <p style={{ marginBottom: 16, color: '#555' }}>
          {branch && (
            <>
              <strong>Branch:</strong> {branch}&emsp;
            </>
          )}
          {(startDate || endDate) && (
            <>
              <strong>Period:</strong> {startDate} to {endDate}
            </>
          )}
        </p>
      )}

      <Table
        columns={columns}
        dataSource={tableData}
        rowKey={(_, idx) => (idx !== undefined ? idx.toString() : '')}
        rowClassName={rowClassName}
        pagination={false}
        scroll={{ x: 'max-content' }}
        size="small"
      />
    </div>
  );
};

export default CashFlowReportDisplay;